/**
 * Sandbox mode — the public demo runs the REAL recruiter dashboard.
 *
 * WHY A MODE ON THE CLIENT, NOT A SEPARATE DEMO PAGE
 * --------------------------------------------------
 * The demo must behave exactly like the dashboard. Building a second rich demo
 * page would fork a 900-line surface that silently rots out of sync, and the
 * whole point is that the demo IS the product. Every drawer, every table, every
 * component already talks to one `request()` on one client — so making that
 * client sandbox-aware turns the entire dashboard, drawers included, into the
 * demo with no changes to any component. New dashboard features appear in the
 * demo for free.
 *
 * READS are real. They hit /recruiter/sandbox/**, a GET-only mirror of the
 * batch API scoped server-side to the one curated demo batch. Real scores, real
 * requirement evidence, real entailment matches — nothing is faked.
 *
 * WRITES never leave the browser. Staging a candidate, bulk-advancing, renaming
 * the position: all of it merges into an overlay in sessionStorage and is
 * replayed over the real reads. The server has no demo write endpoint at all,
 * so two visitors cannot collide and there is nothing to clean up.
 *
 * sessionStorage, not localStorage: the board resets pristine when the tab
 * closes. A stale half-triaged pipeline from three weeks ago is a worse first
 * impression than a clean one.
 */

const KEY = 'fth_sandbox_overlay_v1'

/**
 * The demo's route subtree. Everything the visitor can reach lives under here.
 *
 * The first version reused the dashboard's own links, and every one of them
 * bounced an anonymous visitor to the LOGIN screen: clicking the position in
 * the rail, hitting Compare, opening the full report. The demo is
 * unauthenticated by definition, so any link into /dashboard/* is a dead end.
 */
export const SANDBOX_BASE = '/recruiters/demo'

/** Is this path inside the demo? Sandbox mode is derived from the URL. */
export function isSandboxPath(pathname: string): boolean {
  return pathname === SANDBOX_BASE || pathname.startsWith(SANDBOX_BASE + '/')
    || isSeekerPath(pathname)
}

/**
 * The CANDIDATE side of the same coin (docs/plans/candidate-sandbox-demos.md).
 * Same sandbox client, same read-only server, same overlay — but the report
 * renders in the candidate voice (no `view=recruiter`), and the page under
 * `/demo/report` is the intake replay that lands on it.
 */
export const SEEKER_BASE = '/demo/report'
export function isSeekerPath(pathname: string): boolean {
  return pathname === SEEKER_BASE || pathname.startsWith(SEEKER_BASE + '/')
}
export function isSeeker(): boolean {
  return typeof window !== 'undefined' && isSeekerPath(window.location.pathname)
}
export function seekerReportPath(jobId: string): string {
  return `${SEEKER_BASE}/${jobId}`
}

/**
 * Where recruiter links should point right now.
 *
 * Deriving this from the flag (rather than hardcoding /dashboard/recruiter)
 * lets the SAME components serve the real dashboard and the demo, which is the
 * whole premise — the demo is the product, not a copy of it.
 */
export function recruiterBase(): string {
  return isSandbox() ? SANDBOX_BASE : '/dashboard/recruiter'
}

/** The full-report route for a job, inside the demo or in the real app. */
export function reportPath(jobId: string): string {
  return isSandbox() ? `${SANDBOX_BASE}/report/${jobId}` : `/try-v2/${jobId}`
}

/** Local edits, replayed over real server reads. */
export interface SandboxOverlay {
  candidates: Record<string, { stage?: string; disposition?: string; notes?: string }>
  batch: { job_title?: string; company_name?: string }
}

/**
 * Derived at module load, not only when RecruiterSandbox renders.
 *
 * On a HARD load straight to /recruiters/demo/report/<jobId> — a shared link, a
 * bookmark, a refresh — the report's first effect ran before the layout had set
 * this flag, so `getAnonStatus` took the real ownership-scoped route, got 404
 * "Job not found or access denied", and the page sat on the loading animation
 * forever. Client-side navigation into the same report worked, which is exactly
 * the shape that hides this in testing.
 *
 * The path is knowable the instant the module loads, so read it there. The
 * layout still calls setSandbox on every render to cover client-side
 * navigation both into and out of the demo.
 */
let active = typeof window !== 'undefined'
  && isSandboxPath(window.location.pathname)

export function isSandbox(): boolean {
  // DERIVED FROM THE PATH, never from a flag a component lifecycle owns.
  //
  // This returned `active`, which a page set during render and cleared in an
  // unmount cleanup. So any unmount turned sandbox mode off globally while
  // children were still fetching — and React StrictMode unmounts every
  // component once on purpose in development. The demo's positions rail
  // issued its second fetch with the flag off, hit the authenticated API
  // instead of the read-only mirror, took a 401, and swallowed it: an empty
  // "No positions yet." rail sitting on a perfectly good seeded sandbox.
  //
  // The path is the truth and is already the thing the setter was computing.
  if (typeof window !== 'undefined'
      && isSandboxPath(window.location.pathname)) return true
  return active
}

export function setSandbox(on: boolean): void {
  active = on
}

export function readOverlay(): SandboxOverlay {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) return { candidates: {}, batch: {} }
    const parsed = JSON.parse(raw)
    return {
      candidates: parsed?.candidates ?? {},
      batch: parsed?.batch ?? {},
    }
  } catch {
    // Private mode, quota, corrupt JSON — the demo must still render. Losing
    // local edits is survivable; a blank page is not.
    return { candidates: {}, batch: {} }
  }
}

function writeOverlay(next: SandboxOverlay): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(next))
  } catch {
    /* ignore — see readOverlay */
  }
}

export function resetOverlay(): void {
  try {
    sessionStorage.removeItem(KEY)
    // "Start over" has to put the criteria back too — otherwise the board
    // resets to a ranking the visitor never chose and cannot explain.
    sessionStorage.removeItem(REWEIGHT_KEY)
  } catch {
    /* ignore */
  }
}

/** True when the visitor has actually changed something. Drives the Reset button. */
export function hasEdits(): boolean {
  const o = readOverlay()
  return Object.keys(o.candidates).length > 0
    || Object.keys(o.batch).length > 0
    || reweightOff()
}

export function patchCandidate(
  candidateId: string,
  body: { stage?: string; disposition?: string; notes?: string },
): void {
  const o = readOverlay()
  o.candidates[candidateId] = { ...(o.candidates[candidateId] || {}), ...body }
  writeOverlay(o)
}

export function patchCandidates(
  candidateIds: string[],
  body: { stage?: string; disposition?: string },
): void {
  const o = readOverlay()
  for (const id of candidateIds) {
    o.candidates[id] = { ...(o.candidates[id] || {}), ...body }
  }
  writeOverlay(o)
}

export function patchBatchFields(body: { job_title?: string; company_name?: string }): void {
  const o = readOverlay()
  o.batch = { ...o.batch, ...body }
  writeOverlay(o)
}

/**
 * Replay local edits over a real server payload.
 *
 * Applied on the way OUT of the client so every consumer — table, drawers,
 * filters, stage counts — sees one consistent world without knowing the
 * sandbox exists. `has_notes` is recomputed because the flag chips key on it,
 * and a note you just typed that produced no chip reads as a broken product.
 */
export function applyOverlay<T>(payload: T): T {
  // isSandbox(), not the raw flag: the flag is cleared by an unmount cleanup,
  // and dropping out of sandbox mode here does not fall back to server truth —
  // it silently discards the visitor's own edits, so a staged candidate jumps
  // back to where it was.
  if (!isSandbox() || !payload || typeof payload !== 'object') return payload
  const o = readOverlay()
  const p = payload as Record<string, unknown>

  if (typeof p.job_title === 'string' && o.batch.job_title !== undefined) {
    p.job_title = o.batch.job_title
  }
  if (typeof p.company_name === 'string' && o.batch.company_name !== undefined) {
    p.company_name = o.batch.company_name
  }

  const list = p.candidates
  if (Array.isArray(list)) {
    p.candidates = list.map((c: Record<string, unknown>) => {
      const edit = o.candidates[String(c?.candidate_id)]
      if (!edit) return c
      const merged: Record<string, unknown> = { ...c, ...edit }
      if (edit.notes !== undefined) merged.has_notes = !!edit.notes
      return merged
    })
  }

  // A single-candidate payload (the detail drawer) carries its own id.
  const own = typeof p.candidate_id === 'string' ? o.candidates[p.candidate_id] : undefined
  if (own) {
    Object.assign(p, own)
    if (own.notes !== undefined) p.has_notes = !!own.notes
  }
  return payload
}

/**
 * The one requirement the demo lets a visitor re-weight.
 *
 * Editing criteria is real and cheap — overrides apply at read time over
 * verdicts that are already computed. What it cannot be here is OPEN: there is
 * one demo batch and every visitor shares it, so a free editor would let a
 * stranger re-rank the shortlist someone else is mid-read of.
 *
 * So it is exactly two states, and the server holds both (see
 * `_apply_demo_reweight`). The flag below picks which one this browser sees.
 * Kept OUT of the overlay object because it is not a local edit replayed over a
 * payload — it changes what the server computes, and it has to survive into the
 * request headers.
 */
const REWEIGHT_KEY = 'fth_sandbox_reweight_v1'

export const REWEIGHT_HEADER = 'X-FTH-Demo-Reweight'

/**
 * Which requirement is toggleable — supplied by the server, never hardcoded
 * here. Re-seeding the demo changes the req_ids, and a frontend constant would
 * leave a control wired to a requirement that no longer exists.
 */
let reweightReqIdValue: string | null = null

export function reweightReqId(): string | null {
  return reweightReqIdValue
}

export function setReweightReqId(id: string | null): void {
  reweightReqIdValue = id
}

/**
 * The candidate the demo calls out as the one a keyword screen would drop.
 *
 * DECLARED by the server, not derived from the payload. Deriving it — "most
 * requirements met in her own vocabulary" — looked principled and picked the
 * wrong person: wording_gap counts run 6-8 across the whole cast, so the
 * winner was decided by row order and the tour pointed at a wrong-domain data
 * engineer while the copy described someone else. Which candidate is the
 * rescue is a fact about the cast, not a signal in the scores.
 */
let gemCandidateValue: string | null = null

export function gemCandidate(): string | null {
  return gemCandidateValue
}

export function setGemCandidate(name: string | null): void {
  gemCandidateValue = name
}

export function reweightOff(): boolean {
  try { return sessionStorage.getItem(REWEIGHT_KEY) === '1' } catch { return false }
}

export function setReweightOff(on: boolean): void {
  try {
    if (on) sessionStorage.setItem(REWEIGHT_KEY, '1')
    else sessionStorage.removeItem(REWEIGHT_KEY)
  } catch { /* ignore — see readOverlay */ }
}

/** Canned position brief. */
export function cannedBrief(jobTitle?: string | null): { brief: string; cached: boolean } {
  const role = jobTitle || 'this role'
  return {
    // Deliberately canned rather than proxied to the real generator: the brief
    // is the ONE LLM call on this surface, and an unauthenticated public
    // endpoint that bills per click is not something to ship. Scoring itself is
    // pure CPU, which is why every other number here can be genuinely real.
    brief:
      `Sample brief for ${role}.\n\n` +
      `Your strongest candidates cluster around depth of production ownership ` +
      `rather than tool overlap — several show the required capability under a ` +
      `different vendor's name, which keyword screening would have missed.\n\n` +
      `Two candidates carry readability flags worth a look before you call them: ` +
      `dense skill lists with little supporting evidence in the bullets.\n\n` +
      `In the live product this brief is generated from your actual batch. ` +
      `In the sandbox it is fixed text, so the demo costs nothing to run.`,
    cached: true,
  }
}
